import { isFieldChange, type FieldChange } from "~/contracts/events";
import type { Json } from "~/contracts/json";
import type { CalendarDate } from "~/domain/time/calendar-date";

export const EVENT_LOG_TABLES = [
  "DOMAIN_EVENT",
  "ACTION_LOG",
  "AUTH_EVENT",
] as const;

export type EventLogTable = (typeof EVENT_LOG_TABLES)[number];
export type EventLogStatus = "ok" | "error";
export type JsonObject = { [key: string]: Json };

type EventLogRecordBase = {
  id: string;
  event: string;
  timestamp: number;
  properties: JsonObject;
};

export type DomainEventLogRecord = EventLogRecordBase & {
  table: "DOMAIN_EVENT";
  actorUserId: string | null;
  entity: {
    type: string;
    id: string;
  };
  changes: FieldChange[];
};

export type ActionEventLogRecord = EventLogRecordBase & {
  table: "ACTION_LOG";
  actorUserId: string | null;
  status: EventLogStatus;
  durationMs: number;
};

export type AuthEventLogRecord = EventLogRecordBase & {
  table: "AUTH_EVENT";
  screen: string | null;
  method: string | null;
  outcome: string;
};

export type EventLogRecord =
  | DomainEventLogRecord
  | ActionEventLogRecord
  | AuthEventLogRecord;

export interface EventLogPageInfo {
  endCursor: string | null;
  hasNextPage: boolean;
}

export interface EventLogQueryResult {
  records: EventLogRecord[];
  totalCount: number;
  pageInfo: EventLogPageInfo;
}

export interface EventLogFilters {
  eventType?: string;
  actorUserId?: string;
  status?: EventLogStatus;
  onlyHighRisk?: boolean;
  dateRange?: {
    start?: CalendarDate;
    end?: CalendarDate;
  };
}

export interface EventLogQueryInput {
  table: EventLogTable;
  filters?: EventLogFilters;
  first?: number;
  after?: string | null;
}

export interface EventLogCursor {
  timestamp: number;
  id: string;
}

export type EventLogParseError = {
  message: string;
};

export type EventLogParseResult =
  | { ok: true; value: EventLogRecord }
  | { ok: false; error: EventLogParseError };

export function isEventLogTable(value: string): value is EventLogTable {
  return EVENT_LOG_TABLES.some((table) => table === value);
}

export function isEventLogStatus(value: string): value is EventLogStatus {
  return value === "ok" || value === "error";
}

function isObject(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function isJson(value: unknown): value is Json {
  if (
    value === null ||
    typeof value === "string" ||
    typeof value === "boolean"
  ) {
    return true;
  }

  if (typeof value === "number") {
    return Number.isFinite(value);
  }

  if (Array.isArray(value)) {
    return value.every(isJson);
  }

  if (!isObject(value)) {
    return false;
  }

  return Object.values(value).every(isJson);
}

function isJsonObject(value: unknown): value is JsonObject {
  return isObject(value) && Object.values(value).every(isJson);
}

function isEventLogRecordBase(
  value: Record<string, unknown>,
): value is Record<string, unknown> & EventLogRecordBase {
  return (
    typeof value.id === "string" &&
    typeof value.event === "string" &&
    typeof value.timestamp === "number" &&
    Number.isFinite(value.timestamp) &&
    isJsonObject(value.properties)
  );
}

function isNullableString(value: unknown): value is string | null {
  return value === null || typeof value === "string";
}

function parseEventLogRecord(value: unknown): EventLogParseResult {
  if (!isObject(value) || !isEventLogRecordBase(value)) {
    return {
      ok: false,
      error: { message: "Event log record has an invalid base shape" },
    };
  }

  switch (value.table) {
    case "DOMAIN_EVENT": {
      if (!isNullableString(value.actorUserId) || !isObject(value.entity)) {
        return {
          ok: false,
          error: { message: "Domain event has an invalid actor or entity" },
        };
      }

      if (
        typeof value.entity.type !== "string" ||
        typeof value.entity.id !== "string" ||
        !Array.isArray(value.changes) ||
        !value.changes.every(isFieldChange)
      ) {
        return {
          ok: false,
          error: {
            message: "Domain event has invalid entity fields or changes",
          },
        };
      }

      return {
        ok: true,
        value: {
          id: value.id,
          table: value.table,
          event: value.event,
          timestamp: value.timestamp,
          properties: value.properties,
          actorUserId: value.actorUserId,
          entity: {
            type: value.entity.type,
            id: value.entity.id,
          },
          changes: value.changes,
        },
      };
    }

    case "ACTION_LOG": {
      if (
        !isNullableString(value.actorUserId) ||
        typeof value.status !== "string" ||
        !isEventLogStatus(value.status) ||
        typeof value.durationMs !== "number" ||
        !Number.isFinite(value.durationMs)
      ) {
        return {
          ok: false,
          error: { message: "Action event has invalid action metadata" },
        };
      }

      return {
        ok: true,
        value: {
          id: value.id,
          table: value.table,
          event: value.event,
          timestamp: value.timestamp,
          properties: value.properties,
          actorUserId: value.actorUserId,
          status: value.status,
          durationMs: value.durationMs,
        },
      };
    }

    case "AUTH_EVENT": {
      if (
        !isNullableString(value.screen) ||
        !isNullableString(value.method) ||
        typeof value.outcome !== "string"
      ) {
        return {
          ok: false,
          error: { message: "Authentication event has invalid metadata" },
        };
      }

      return {
        ok: true,
        value: {
          id: value.id,
          table: value.table,
          event: value.event,
          timestamp: value.timestamp,
          properties: value.properties,
          screen: value.screen,
          method: value.method,
          outcome: value.outcome,
        },
      };
    }

    default:
      return {
        ok: false,
        error: { message: "Event log record has an unknown table" },
      };
  }
}

export function parseEventLogRecordText(raw: string): EventLogParseResult {
  try {
    return parseEventLogRecord(JSON.parse(raw));
  } catch {
    return {
      ok: false,
      error: { message: "Event log payload is not valid JSON" },
    };
  }
}

export function encodeEventLogCursor(cursor: EventLogCursor): string {
  return btoa(`${cursor.timestamp}:${cursor.id}`);
}

export function decodeEventLogCursor(raw: string): EventLogCursor | null {
  try {
    const decoded = atob(raw);
    const separator = decoded.indexOf(":");

    if (separator < 0) {
      return null;
    }

    const timestamp = Number(decoded.slice(0, separator));
    const id = decoded.slice(separator + 1);

    if (!Number.isFinite(timestamp) || id.length === 0) {
      return null;
    }

    return { timestamp, id };
  } catch {
    return null;
  }
}
